import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { useFailedMessagesQuery } from '../../../hooks/admin/useSystemHealth'
import { FAILED_RANGES, FAILED_DEFAULT_DAYS } from '../../../constants/systemHealth'
import type { SystemHealthFailed } from '../../../types/systemHealth'
import { cn } from '../../../shared/utils/cn'

/**
 * Messages that left the pipeline terminally — STATUS_CODE 6 (permanent) or
 * 7 (retries exhausted).
 *
 * This section exists because nothing else on the page showed them. The banner's
 * "unprocessed" is STATUS_CODE 0, i.e. work still queued, so a message that fails
 * terminally drops out of every counter the moment it is parked. That is how the
 * 2026-04-21 SNTS decoder regression discarded 1,631 messages across 54 devices
 * for four months in silence — the only outward symptom was battery telemetry
 * that quietly stopped arriving.
 *
 * These are not retried. The stage-2 backlog query selects STATUS_CODE 0 only,
 * so a parked row stays invisible to the pipeline until something deliberately
 * re-arms it (scripts/reprocess-decode-failures.ts in the processor repo). The
 * raw rows are retained forever, so nothing here is lost — but nothing here
 * recovers on its own either, which is exactly why it needs to be on screen.
 */
export function FailedMessagesSection() {
  const [days, setDays] = useState(FAILED_DEFAULT_DAYS)
  const { data, isLoading } = useFailedMessagesQuery(days)

  if (isLoading) {
    return (
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Failed messages</h2>
        <p className="text-sm text-gray-500">Loading…</p>
      </section>
    )
  }

  if (!data || data.total === 0) {
    return (
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <Header days={days} setDays={setDays} />
        <p className="text-sm text-green-700">
          <FontAwesomeIcon icon={faCircleCheck} className="mr-1.5 h-4 w-4" />
          No messages have failed permanently.
        </p>
      </section>
    )
  }

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <Header days={days} setDays={setDays} />

      <dl className="grid grid-cols-3 gap-4">
        <Stat label={rangeLabel(days)} value={data.windowed} highlight={data.windowed > 0} />
        <Stat label="Devices affected" value={data.devices} />
        <Stat label="Distinct causes" value={data.reasons.length || data.byType.length} />
      </dl>

      {data.reasons.length > 0 && <Reasons reasons={data.reasons} />}

      <Breakdown byType={data.byType} />
      <Footnote total={data.total} oldest={data.oldest} windowed={data.windowed} />
    </section>
  )
}

/**
 * Keeps the all-time total on screen at every window.
 *
 * The tiles above are scoped, so at the 3-day default the card reads 32 while
 * 1,713 sit behind it. A card that can hide a backlog is how the SNTS
 * regression stayed invisible for four months — the total stays visible, just
 * not as a headline competing with the number you actually selected.
 */
function Footnote({
  total,
  oldest,
  windowed,
}: {
  total: number
  oldest: number
  windowed: number
}) {
  if (total === windowed) return null

  return (
    <p className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
      <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1.5 h-3 w-3 text-red-700" />
      <span className="font-medium text-red-700">{total.toLocaleString()}</span> failed in total
      {oldest > 0 && `, back to ${new Date(oldest * 1000).toISOString().slice(0, 10)}`} — select
      All to see every one.
    </p>
  )
}

function rangeLabel(days: number): string {
  if (days === 0) return 'All time'
  const preset = FAILED_RANGES.find((r) => r.days === days)
  return preset && days >= 365 ? 'Last year' : `Last ${days} days`
}

function Header({ days, setDays }: { days: number; setDays: (d: number) => void }) {
  return (
    <div className="flex items-baseline justify-between gap-4 flex-wrap mb-4">
      <h2 className="text-lg font-semibold text-gray-900">Failed messages</h2>
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-xs text-gray-500">
          Not retried automatically — these need a deliberate reprocess.
        </p>
        <div className="flex gap-1" role="group" aria-label="Failed message time range">
          {FAILED_RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              aria-pressed={days === r.days}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                days === r.days
                  ? 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function Reasons({ reasons }: { reasons: SystemHealthFailed['reasons'] }) {
  return (
    <div className="mt-6">
      <h4 className="text-sm font-medium text-gray-700">Reported causes</h4>
      <p className="mt-1 text-xs text-gray-500">
        From dead-letter records, which only cover failures recorded since the fast-fail
        change — older parked messages have no stored reason.
      </p>
      <ul className="mt-2 space-y-1">
        {reasons.map((r) => (
          <li key={r.reason} className="flex items-baseline gap-3 text-sm">
            <span className="font-mono text-xs text-red-700 tabular-nums">{r.count}</span>
            <span className="text-gray-500 text-xs tabular-nums">{r.devices} dev</span>
            <span className="text-gray-800 break-all">{r.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Breakdown({ byType }: { byType: SystemHealthFailed['byType'] }) {
  const fmt = (epoch: number) =>
    epoch ? new Date(epoch * 1000).toISOString().slice(0, 10) : '—'

  return (
    <div className="mt-6 overflow-x-auto">
      <h4 className="text-sm font-medium text-gray-700">By message type</h4>
      <table className="mt-2 min-w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-gray-500">
            <th className="py-1 pr-4 text-left font-medium">Type</th>
            <th className="py-1 pr-4 text-left font-medium">Message</th>
            <th className="py-1 pr-4 text-left font-medium">Status</th>
            <th className="py-1 pr-4 text-right font-medium">Count</th>
            <th className="py-1 pr-4 text-right font-medium">Devices</th>
            <th className="py-1 pr-4 text-left font-medium">Oldest</th>
            <th className="py-1 text-left font-medium">Newest</th>
          </tr>
        </thead>
        <tbody>
          {byType.map((t) => (
            <tr key={`${t.msgType}-${t.statusCode}`} className="border-t border-gray-100">
              <td className="py-1.5 pr-4 font-mono text-gray-600">0x{t.msgType}</td>
              <td className="py-1.5 pr-4 font-medium">{t.msgTypeName}</td>
              <td className="py-1.5 pr-4">
                {t.statusCode === 6 ? 'permanent' : 'retries exhausted'}
              </td>
              <td className="py-1.5 pr-4 text-right font-medium tabular-nums text-red-700">
                {t.count.toLocaleString()}
              </td>
              <td className="py-1.5 pr-4 text-right tabular-nums">{t.devices}</td>
              <td className="py-1.5 pr-4 tabular-nums text-gray-600">{fmt(t.oldest)}</td>
              <td className="py-1.5 tabular-nums text-gray-600">{fmt(t.newest)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string
  value: number
  highlight?: boolean
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd
        className={
          'text-2xl font-semibold tabular-nums ' +
          (highlight && value > 0 ? 'text-red-700' : 'text-gray-900')
        }
      >
        {value.toLocaleString()}
        {highlight && value > 0 && (
          <FontAwesomeIcon icon={faTriangleExclamation} className="ml-2 h-4 w-4" />
        )}
      </dd>
    </div>
  )
}
